import type { ChartPanel } from '../types'

const COLORS = ['#4f6bed', '#14a38b', '#e98b2a', '#9a5be8', '#e25572', '#3d91c9']
const WIDTH = 920
const HEIGHT = 330
const MARGIN = { top: 26, right: 18, bottom: 68, left: 68 }

type BarChartProps = {
  panel: ChartPanel
  sharedMaximum?: number
}

function compactNumber(value: number) {
  return new Intl.NumberFormat('ko-KR', {
    notation: value >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}

export default function BarChart({ panel, sharedMaximum }: BarChartProps) {
  const plotWidth = WIDTH - MARGIN.left - MARGIN.right
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom
  const localMaximum = Math.max(1, ...panel.series.flatMap((series) => series.values))
  const maximum = Math.max(1, sharedMaximum ?? localMaximum)
  const groupWidth = plotWidth / Math.max(1, panel.labels.length)
  const innerWidth = Math.min(groupWidth * 0.76, 72)
  const barWidth = innerWidth / Math.max(1, panel.series.length)
  const ticks = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`${panel.title} 노출수 차트`}>
        {ticks.map((ratio) => {
          const y = MARGIN.top + plotHeight - plotHeight * ratio
          return (
            <g key={ratio}>
              <line
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                y1={y}
                y2={y}
                className="chart-grid-line"
              />
              <text x={MARGIN.left - 12} y={y + 4} textAnchor="end" className="chart-axis-text">
                {compactNumber(maximum * ratio)}
              </text>
            </g>
          )
        })}

        {panel.labels.map((label, labelIndex) => {
          const groupStart = MARGIN.left + labelIndex * groupWidth + (groupWidth - innerWidth) / 2
          return (
            <g key={`${label}-${labelIndex}`}>
              {panel.series.map((series, seriesIndex) => {
                const value = series.values[labelIndex] ?? 0
                const height = (value / maximum) * plotHeight
                const x = groupStart + seriesIndex * barWidth
                const y = MARGIN.top + plotHeight - height
                return (
                  <rect
                    key={series.key}
                    x={x + 1}
                    y={y}
                    width={Math.max(1, barWidth - 2)}
                    height={height}
                    rx={3}
                    fill={COLORS[seriesIndex % COLORS.length]}
                  >
                    <title>{`${label} · ${series.label}: ${value.toLocaleString('ko-KR')}`}</title>
                  </rect>
                )
              })}
              <text
                x={MARGIN.left + labelIndex * groupWidth + groupWidth / 2}
                y={HEIGHT - 38}
                textAnchor="end"
                transform={`rotate(-28 ${MARGIN.left + labelIndex * groupWidth + groupWidth / 2} ${HEIGHT - 38})`}
                className="chart-axis-text"
              >
                {label.length > 12 ? `${label.slice(0, 11)}…` : label}
              </text>
            </g>
          )
        })}
      </svg>

      <div className="chart-legend" aria-label="범례">
        {panel.series.map((series, index) => (
          <span key={series.key}>
            <i style={{ backgroundColor: COLORS[index % COLORS.length] }} />
            {series.label}
          </span>
        ))}
      </div>
    </div>
  )
}
